import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import {
  feedbackSelectors,
  getAllFeedbacks,
  createFeedback,
  updateFeedback,
  feedbackApi,
} from 'entities/feedback';
import { FeedbackRequestDto, FeedbackResponseDto } from 'shared/api';
import { useGlobalSpinner } from 'shared/hooks';
import { deleteFeedback } from 'entities/feedback/model/actions/actions';

export const useFeedback = () => {
  const dispatch = useAppDispatch();
  const feedbacks = useAppSelector(feedbackSelectors.getFeedbacks);
  const isLoading = useAppSelector(feedbackSelectors.getFeedbacksLoading);
  const { showSpinner, hideSpinner } = useGlobalSpinner();

  const [feedbackDetail, setFeedbackDetail] = useState<FeedbackResponseDto | null>(null);
  const [feedbackDetailError, setFeedbackDetailError] = useState<unknown>(null);

  const loadFeedbacks = useCallback(() => {
    showSpinner('Загрузка отзывов...');
    dispatch(getAllFeedbacks()).finally(() => {
      hideSpinner();
    });
  }, [dispatch, showSpinner, hideSpinner]);

  const createNewFeedback = useCallback(async (data: FeedbackRequestDto) => {
    try {
      showSpinner('Создание отзыва...');
      await dispatch(createFeedback(data)).unwrap();
      loadFeedbacks();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadFeedbacks, showSpinner, hideSpinner]);

  const loadFeedbackDetail = useCallback(async (id: number) => {
    setFeedbackDetailError(null);
    try {
      showSpinner('Загрузка данных отзыва...');
      const detail = await feedbackApi.getFeedbackById(id);
      setFeedbackDetail(detail);
      return { success: true, detail } as const;
    } catch (error) {
      setFeedbackDetailError(error);
      return { success: false, error } as const;
    } finally {
      hideSpinner();
    }
  }, [showSpinner, hideSpinner]);

  const updateExistingFeedback = useCallback(async (id: number, data: FeedbackRequestDto) => {
    try {
      showSpinner('Обновление отзыва...');
      await dispatch(updateFeedback({ id, data })).unwrap();
      loadFeedbacks();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadFeedbacks, showSpinner, hideSpinner]);

  const deleteFeedbackById = useCallback(async (id: number) => {
    try {
      showSpinner('Удаление отзыва...');
      await dispatch(deleteFeedback(id)).unwrap();
      loadFeedbacks();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadFeedbacks, showSpinner, hideSpinner]);

  return {
    feedbacks,
    isLoading,
    loadFeedbacks,
    createNewFeedback,
    updateExistingFeedback,
    feedbackDetail,
    feedbackDetailError,
    loadFeedbackDetail,
    deleteFeedbackById,
  };
};
